'use client';
import { useResource } from '@/lib/use-resource';

interface RiskRow {
  symbol: string;
  weight: number;
  pctOfRisk: number;
  contribution: number;
}

interface SnapshotData {
  current: {
    expectedReturn: number;
    expectedStdev: number;
    expectedSharpe: number;
  };
  cash: { weight: number; earns: number };
  fees: { mgmtFee: number; perfFee: number; hurdle: number };
  grossWithCash: {
    expectedReturn: number;
    expectedStdev: number;
    expectedSharpe: number;
  };
  netOfFees: {
    netReturn: number;
    netSharpe: number;
    mgmtCost: number;
    perfCost: number;
    totalDrag: number;
  };
  portfolioBeta: number | null;
  concentration: { top1: number; top3: number; top5: number; top10: number };
  riskContributions: RiskRow[];
}

const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;
const fmtSharpe = (v: number) => v.toFixed(2);

export function PortfolioAnalytics({ mode = 'main', dataUrl = '/api/portfolio-snapshot', title = 'Portfolio Analytics' }: { mode?: 'main' | 'risk'; dataUrl?: string; title?: string }) {
  const { data: d } = useResource<SnapshotData>(dataUrl);

  if (!d) return null;

  const cashLine = d.cash.weight > 0 ? ` (incl. ${(d.cash.weight * 100).toFixed(0)}% cash)` : '';
  const top5Bar = Math.min(100, d.concentration.top5 * 100);

  // Risk-decomposition variant: render only the per-symbol contribution table.
  if (mode === 'risk') {
    return (
      <section className="mb-6 space-y-4">
        <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">Risk Decomposition</h2>
        <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
          <div className="px-4 py-2 text-[10px] uppercase tracking-wide text-[#94a3b8] border-b border-[#1e293b] flex items-center justify-between">
            <span>Who drives portfolio σ</span>
            <span className="text-[#64748b] normal-case tracking-normal">
              σ = {fmtPct(d.grossWithCash.expectedStdev)} · contributions sum to 100%
            </span>
          </div>
          <div className="px-4 py-2 grid grid-cols-12 gap-2 text-[9px] uppercase tracking-wider text-[#64748b] border-b border-[#1e293b]/60">
            <div className="col-span-2">Symbol</div>
            <div className="col-span-2 text-right">Weight</div>
            <div
              className="col-span-2 text-right cursor-help"
              title="% of Risk = weight × covariance with the rest of the book ÷ total portfolio variance. A 14% holding contributing 19% of risk is amplifying — its co-movement with the other big positions absorbs more variance than its weight alone implies."
            >
              % of Risk
            </div>
            <div className="col-span-1 text-right">Ratio</div>
            <div className="col-span-5 text-left pl-2">Risk share</div>
          </div>
          {d.riskContributions.slice(0, 12).map((r) => {
            const ratio = r.weight > 0 ? r.pctOfRisk / r.weight : 0;
            const tone = ratio > 1.3 ? 'text-red-400' : ratio < 0.7 ? 'text-emerald-400' : 'text-[#cbd5e1]';
            const barPct = Math.min(100, r.pctOfRisk * 100 * 2.5);
            return (
              <div key={r.symbol} className="px-4 py-1 grid grid-cols-12 gap-2 text-[11px] border-b border-[#1e293b]/40 hover:bg-[#1e293b]/30">
                <div className="col-span-2 font-mono font-bold text-white">{r.symbol}</div>
                <div className="col-span-2 text-right tabular-nums text-[#cbd5e1]">{fmtPct(r.weight)}</div>
                <div className="col-span-2 text-right tabular-nums font-semibold text-amber-300">{fmtPct(r.pctOfRisk)}</div>
                <div className={`col-span-1 text-right tabular-nums text-[10px] ${tone}`} title="ratio = pctRisk / weight; >1 = risk-amplifier, <1 = diversifier">
                  {ratio.toFixed(2)}×
                </div>
                <div className="col-span-5 flex items-center pl-2">
                  <div className="h-2 bg-amber-400/60 rounded-sm" style={{ width: `${barPct}%` }} />
                </div>
              </div>
            );
          })}
          <div className="px-4 py-2 text-[10px] text-[#64748b] space-y-1">
            <div>
              <span className="text-[#cbd5e1] font-semibold">% of Risk</span> = weight × covariance with the rest of the book ÷ total variance. The percentages sum to 100%.
            </div>
            <div>
              <span className="text-red-400">Risk-amplifiers</span> (ratio &gt; 1.3) take more risk than their weight implies.{' '}
              <span className="text-emerald-400">Diversifiers</span> (ratio &lt; 0.7) carry less.
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">{title}</h2>

      {/* Gross / Cash-adj / Net of fees comparison */}
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-[#94a3b8] border-b border-[#1e293b] flex items-center justify-between">
          <span>Gross → Cash-adjusted → Net of fees</span>
          <span className="text-[#64748b] normal-case tracking-normal text-[9px]">
            mgmt {fmtPct(d.fees.mgmtFee)} · perf {fmtPct(d.fees.perfFee)} from {fmtPct(d.fees.hurdle)}
          </span>
        </div>
        <div className="grid grid-cols-3 divide-x divide-[#1e293b]">
          <FeesCol label="Gross fully-invested" hint="100% in stocks, no fees" eR={d.current.expectedReturn} sigma={d.current.expectedStdev} sharpe={d.current.expectedSharpe} />
          <FeesCol label={`Cash-adjusted${cashLine}`} hint={`${(d.cash.weight * 100).toFixed(0)}% cash earning ${fmtPct(d.cash.earns)}`} eR={d.grossWithCash.expectedReturn} sigma={d.grossWithCash.expectedStdev} sharpe={d.grossWithCash.expectedSharpe} />
          <FeesCol label="Net of fees" hint={`drag ${(d.netOfFees.totalDrag * 100).toFixed(2)}pp`} eR={d.netOfFees.netReturn} sigma={d.grossWithCash.expectedStdev} sharpe={d.netOfFees.netSharpe} highlight />
        </div>
        <div className="px-3 py-1 text-[9px] text-[#64748b] border-t border-[#1e293b]">
          Mgmt fee: {fmtPct(d.netOfFees.mgmtCost)} · Perf fee: {fmtPct(d.netOfFees.perfCost)} · σ effectively unchanged by fees
        </div>
      </div>

      {/* Beta + concentration grid */}
      <div className="grid md:grid-cols-2 gap-2">
        <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-3">
          <div className="text-[9px] uppercase tracking-wide text-[#94a3b8] mb-1">Beta to SPY</div>
          <div className="flex items-baseline gap-3">
            <span className="text-[12px] font-bold tabular-nums text-white leading-none">
              {d.portfolioBeta == null ? '—' : d.portfolioBeta.toFixed(2)}
            </span>
            <span className="text-[10px] text-[#64748b]">
              {d.portfolioBeta == null ? 'no SPY history available' :
                d.portfolioBeta > 1.10 ? 'aggressive: more cyclical than SPY' :
                d.portfolioBeta < 0.90 ? 'defensive: less cyclical than SPY' :
                'roughly market-like'}
            </span>
          </div>
          <div className="text-[9px] text-[#64748b] mt-1 leading-snug">
            Weighted-average β vs SPY over 252-day window. Cash excluded.
          </div>
        </div>

        <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-[#94a3b8] mb-1">Concentration</div>
          <div className="space-y-0.5 text-[11px] leading-tight">
            <ConcRow label="Top 1 (largest position)" value={d.concentration.top1} />
            <ConcRow label="Top 3" value={d.concentration.top3} />
            <ConcRow label="Top 5" value={d.concentration.top5} highlight />
            <ConcRow label="Top 10" value={d.concentration.top10} />
          </div>
          <div className="mt-1.5 h-1 bg-[#1e293b] rounded">
            <div className="h-full bg-amber-400/60 rounded" style={{ width: `${top5Bar}%` }} />
          </div>
          <div className="text-[9px] text-[#64748b] mt-0.5">Top 5 = {fmtPct(d.concentration.top5)} of book</div>
        </div>
      </div>

    </section>
  );
}

function FeesCol({ label, hint, eR, sigma, sharpe, highlight = false }: { label: string; hint: string; eR: number; sigma: number; sharpe: number; highlight?: boolean }) {
  return (
    <div className="px-3 py-2 leading-tight">
      <div className="text-[9px] uppercase tracking-wide text-[#94a3b8]">{label}</div>
      <div className="text-[9px] text-[#64748b] mb-1">{hint}</div>
      <div className="grid grid-cols-3 gap-1">
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[#64748b]">E[r]</div>
          <div className="text-[12px] tabular-nums font-bold text-white">{fmtPct(eR)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[#64748b]">σ</div>
          <div className="text-[12px] tabular-nums font-bold text-white">{fmtPct(sigma)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[#64748b]">Sharpe</div>
          <div className={`text-[12px] tabular-nums font-bold ${highlight ? 'text-amber-400' : 'text-white'}`}>{fmtSharpe(sharpe)}</div>
        </div>
      </div>
    </div>
  );
}

function ConcRow({ label, value, highlight = false }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={`text-[#94a3b8] ${highlight ? 'text-[#cbd5e1] font-semibold' : ''}`}>{label}</span>
      <span className={`tabular-nums ${highlight ? 'text-amber-400 font-bold' : 'text-[#cbd5e1]'}`}>{fmtPct(value)}</span>
    </div>
  );
}
